import { useEffect, useMemo, useState } from "react";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  FormControl,
  Chip,
  Avatar,
  alpha,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import { Select as AntdSelect } from "antd";

import SearchIcon from "@mui/icons-material/Search";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";

import { RegistrationForm } from "../Registration/index";

import {
  ADMIN_RESET_PASSWORD_API_URL,
  APPROVAL_USERS_ACCOUNT_API_URL,
  APPROVAL_USERS_API_URL,
  GET_ALL_ROLES_API_URL,
  PROJECTS_BY_ACCOUNT_API_URL,
  USER_DEACTIVATE_API_URL,
  USER_UPDATE_API_BASE_URL,
} from "../../config/constants";

import { requestNotificationsRefresh } from "../../services/notificationsApi";
import { useAppDispatch } from "../../store/hooks";
import { setPendingUserCount } from "../../store/slices/pendingUsersSlice";

import {
  mapApiUserToPendingUser,
  parseAccountOptions,
  parseApiUsers,
  parseProjectOptions,
  parseRoleOptions,
} from "./adminUserManagement.utils";

import { PillPagination } from "../../components/PillPagination";
import DataState from "../../components/DataState/DataState";
import "../../styles/trackon.css";
import { apiFetch, ignoreHttpErrors } from "../../utils/apiClient";

const DELIVERY_MANAGER = "Delivery Manager";
const PROJECT_MANAGER = "Project Manager";
const CLIENT_PARTNER = "Client Partner";
const DM_PM_ROLE_ID = 5;

const MULTI_ACCOUNT_ROLES = new Set([
  CLIENT_PARTNER,
  DELIVERY_MANAGER,
]);

const ACCOUNT_PROJECT_ROLES = new Set([
  PROJECT_MANAGER,
]);

const NO_ASSIGNMENT_ROLES = new Set([
  "Account Manager",
  "PMO",
  "Admin",
]);

const isDmPmCombo = (user) => Boolean(user?.alsoPm);

const isMultiAccountRole = (user) =>
  Boolean(user?.alsoPm) || MULTI_ACCOUNT_ROLES.has(user?.role);

const requiresProjects = (user) =>
  Boolean(user?.alsoPm) || ACCOUNT_PROJECT_ROLES.has(user?.role);

const getSelectedRoles = (user) =>
  user?.alsoPm
    ? [DELIVERY_MANAGER, PROJECT_MANAGER]
    : user?.role
      ? [user.role]
      : [];

const normalizeRoleSelection = (selectedValues, roleOptions) => {
  const selected = (selectedValues || []).map(String).filter(Boolean);
  const hasDm = selected.includes(DELIVERY_MANAGER);
  const hasPm = selected.includes(PROJECT_MANAGER);

  if (hasDm && hasPm) {
    return {
      role: DELIVERY_MANAGER,
      roles: [DELIVERY_MANAGER, PROJECT_MANAGER],
      roleId: DM_PM_ROLE_ID,
      alsoPm: true,
    };
  }

  const role = selected[0] || "";
  const roleId =
    roleOptions.find((r) => String(r.name) === role)?.id || 0;

  return {
    role,
    roles: role ? [role] : [],
    roleId: Number(roleId || 0),
    alsoPm: false,
  };
};

const getRoleChangePatch = (currentUser, selectedValues, roleOptions) => {
  const normalized = normalizeRoleSelection(selectedValues, roleOptions);
  const currentAccounts = Array.isArray(currentUser?.assignedAccounts)
    ? currentUser.assignedAccounts.map(String)
    : [];
  const currentProjects = Array.isArray(currentUser?.assignedProjects)
    ? currentUser.assignedProjects.map(String)
    : [];

  const nextRole = normalized.role;
  const nextIsCombo = normalized.alsoPm;
  const nextNeedsMultipleAccounts =
    nextIsCombo || MULTI_ACCOUNT_ROLES.has(nextRole);
  const nextNeedsProjects =
    nextIsCombo || ACCOUNT_PROJECT_ROLES.has(nextRole);

  let assignedAccounts = currentAccounts;
  let assignedProjects = currentProjects;

  if (NO_ASSIGNMENT_ROLES.has(nextRole)) {
    assignedAccounts = [];
    assignedProjects = [];
  } else if (ACCOUNT_PROJECT_ROLES.has(nextRole) && !nextIsCombo) {
    // PM-only supports one account. Preserve the existing assignment instead
    // of clearing it when DM -> PM or another role -> PM.
    assignedAccounts = currentAccounts.slice(0, 1);
    assignedProjects = currentProjects;
  } else if (nextNeedsMultipleAccounts) {
    // DM, CP and DM+PM support the existing account assignments.
    assignedAccounts = currentAccounts;
    assignedProjects = nextNeedsProjects ? currentProjects : [];
  } else {
    assignedAccounts = currentAccounts;
    assignedProjects = [];
  }

  return {
    ...normalized,
    assignedAccounts,
    assignedProjects,
  };
};

const isRoleOptionDisabled = (optionName, selectedValues) => {
  const selected = (selectedValues || []).map(String);

  if (selected.length === 0) return false;

  const hasDm = selected.includes(DELIVERY_MANAGER);
  const hasPm = selected.includes(PROJECT_MANAGER);

  if (hasDm || hasPm) {
    return ![DELIVERY_MANAGER, PROJECT_MANAGER].includes(optionName);
  }

  return !selected.includes(optionName);
};

const theme = createTheme({
  palette: {
    primary: { main: "#2563EB" },
    error: { main: "#EF4444" },
    warning: { main: "#F59E0B" },
    success: { main: "#10B981" },
    background: { default: "#F0F4F8", paper: "#FFFFFF" },
    text: { primary: "#3c3c3e", secondary: "#64748B" },
    divider: "#E2E8F0",
  },

  typography: {
    fontFamily:
      "'Segoe UI Variable Text', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",

    h5: {
      fontWeight: 700,
      fontSize: "1.125rem",
      letterSpacing: "-0.015em",
    },

    body1: {
      fontSize: "0.8125rem",
    },

    body2: {
      fontSize: "0.8125rem",
    },

    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.35,
    },

    overline: {
      fontSize: "0.6875rem",
      fontWeight: 700,
      letterSpacing: "0.05em",
    },
  },

  components: {
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontSize: "0.6875rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "#2D3A5E",
        },

        body: {
          fontSize: "0.8125rem",
          color: "#5C6070",
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          fontWeight: 600,
          minHeight: 42,
          paddingLeft: 12,
          paddingRight: 12,
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          fontWeight: 600,
          lineHeight: 1.3,
          paddingTop: 5,
          paddingBottom: 5,
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: "0.875rem",
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
          fontWeight: 600,
          height: 22,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
        },
      },
    },
  },
});

const getAuthHeader = () => {
  const rawToken =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("accessToken");

  const tokenType = sessionStorage.getItem("tokenType") || "Bearer";

  if (!rawToken) {
    throw new Error("Authentication token not found. Please log in again.");
  }

  const normalizedToken = rawToken
    .replace(/^"|"$/g, "")
    .replace(/^Bearer\s+/i, "")
    .trim();

  return `${tokenType} ${normalizedToken}`;
};

const toStatus = (status) => {
  const normalized = String(status || "").toUpperCase();

  if (normalized === "APPROVED" || normalized === "ACTIVE") {
    return "approved";
  }

  if (normalized === "LOCKED") {
    return "locked";
  }

  return "pending";
};

const statusChip = {
  pending: {
    label: "Pending",
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#FDE68A",
  },

  approved: {
    label: "Active",
    color: "#059669",
    bg: "#F0FDF4",
    border: "#86EFAC",
  },

  locked: {
    label: "Locked",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
  },
};

/*
 * Reusable Ant Design Select wrapper.
 *
 * The actual visual styling is handled by the scoped
 * CSS classes in trackon.css.
 */
function AdminSelect({
  className = "",
  popupClassName = "",
  ...props
}) {
  return (
    <AntdSelect
      {...props}
      className={`admin-user-antd-select ${className}`}
      popupClassName={`admin-user-antd-select-dropdown ${popupClassName}`}
      showSearch={false}
      virtual={false}
    />
  );
}

function KpiCard(props) {
  const { label, count, sub, color, bg, icon, active, onClick } = props;

  return (
    <Card
      onClick={onClick}
      sx={{
        flex: 1,
        minWidth: 180,
        cursor: onClick ? "pointer" : "default",
        border: "1px solid #dbe3ef",
        borderRadius: "12px",
        background:
          "linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)",
        borderTop: `3px solid ${active ? color : "transparent"}`,
        transition: "all .18s ease",
        boxShadow: active
          ? `0 8px 24px ${color}1f`
          : "0 8px 24px rgba(15, 23, 42, 0.05)",

        "&:hover": onClick
          ? {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(0,0,0,.1)",
          }
          : undefined,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box
          sx={{
            width: 36,
            height: 3,
            borderRadius: 99,
            bgcolor: color,
            opacity: 0.8,
            mb: 1.25,
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: bg,
              borderRadius: 2,
              p: 1.125,
            }}
          >
            {icon}
          </Box>

          <Typography
            sx={{
              color,
              fontSize: "1.875rem",
              fontWeight: 700,
              letterSpacing: "-0.03em",
              lineHeight: 1,
            }}
          >
            {count}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            color: "#667085",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontFamily: "inherit",
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            fontSize: "0.75rem",
            color: "#475467",
          }}
        >
          {sub}
        </Typography>
      </CardContent>
    </Card>
  );
}

export function AdminUserManagement() {
  const dispatch = useAppDispatch();

  const currentUserUid = (
    sessionStorage.getItem("userId") || ""
  ).trim();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [usersError, setUsersError] = useState("");

  const [activeTab, setActiveTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [roles, setRoles] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [projectsByUser, setProjectsByUser] = useState({});
  const [projectsLoadingByUser, setProjectsLoadingByUser] =
    useState({});

  const [editUser, setEditUser] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [deleteUser, setDeleteUser] = useState(null);

  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState("");

  const [addUserOpen, setAddUserOpen] = useState(false);

  const accountNameById = useMemo(() => {
    const map = new Map();

    accounts.forEach((a) => {
      map.set(String(a.id), a.name);
    });

    return map;
  }, [accounts]);

  const pending = useMemo(
    () => users.filter((u) => u.status === "pending"),
    [users],
  );

  const approved = useMemo(
    () => users.filter((u) => u.status === "approved"),
    [users],
  );

  const locked = useMemo(
    () => users.filter((u) => u.status === "locked"),
    [users],
  );

  const tabUsers =
    activeTab === "pending"
      ? pending
      : activeTab === "approved"
        ? approved
        : locked;

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();

    if (!q) return tabUsers;

    return tabUsers.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.uid.toLowerCase().includes(q),
    );
  }, [search, tabUsers]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / rowsPerPage),
  );

  const paginated = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  // Loading before the first load and during every reload, including Retry.
  const status =
    loading || !usersLoaded
      ? "loading"
      : usersError
        ? "error"
        : "ready";

  const fetchUsers = async () => {
    setLoading(true);
    setUsersError("");

    try {
      const res = await apiFetch(APPROVAL_USERS_API_URL, {
        headers: {
          Authorization: getAuthHeader(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }, "Failed to fetch users");

      if (!res.ok) throw new Error("Failed to fetch users");

      const data = await res.json();
      const parsedUsers = parseApiUsers(data);

      const rawUsers = Array.isArray(data)
        ? data
        : Array.isArray(data?.users)
          ? data.users
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.content)
              ? data.content
              : [];

      const rawByUid = new Map(
        rawUsers.map((item) => [
          String(item?.userId || item?.uid || item?.id || ""),
          item,
        ]),
      );

      const mapped = parsedUsers
        .map(mapApiUserToPendingUser)
        .map((u) => {
          const raw = rawByUid.get(String(u.uid)) || {};
          const alsoPm = Boolean(raw.alsoPm ?? u.alsoPm);
          const roleName =
            raw.roleName || raw.role || u.role || "";
          const role = alsoPm ? DELIVERY_MANAGER : roleName;
          const roleId = alsoPm
            ? DM_PM_ROLE_ID
            : Number(raw.roleId ?? u.roleId ?? 0);

          const assignedAccounts = (
            raw.assignedAccounts ??
            raw.accountIds ??
            u.accountIds ??
            []
          ).map(String);

          const assignedProjects = (
            role === DELIVERY_MANAGER || role === CLIENT_PARTNER
              ? (alsoPm
                ? (
                  raw.assignedProjects ??
                  raw.projectIds ??
                  u.projectIds ??
                  []
                ).map(String)
                : [])
              : (
                raw.assignedProjects ??
                raw.projectIds ??
                u.projectIds ??
                []
              ).map(String)
          );

          return {
            id: u.id || raw.id || raw.userId || u.uid,
            name: `${raw.firstName || u.firstName || ""
              } ${raw.lastName || u.lastName || ""}`.trim(),
            uid: u.uid || raw.userId || raw.uid,
            status: toStatus(raw.status || u.status),
            role,
            roles: alsoPm
              ? [DELIVERY_MANAGER, PROJECT_MANAGER]
              : role
                ? [role]
                : [],
            roleId,
            alsoPm,
            assignedAccounts,
            assignedProjects,
            avatar: (
              (
                raw.firstName ||
                u.firstName ||
                raw.userId ||
                u.uid ||
                "U"
              ).charAt(0) || "U"
            ).toUpperCase(),
          };
        });

      setUsers(mapped);

      const pendingCount = mapped.filter(
        (user) => user.status === "pending",
      ).length;

      dispatch(setPendingUserCount(pendingCount));
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsers([]);
      setUsersError(
        error instanceof Error && error.message
          ? error.message
          : "Please check your connection and try again.",
      );
    } finally {
      setLoading(false);
      setUsersLoaded(true);
    }
  };

  useEffect(() => {
    void fetchUsers();

    void (async () => {
      try {
        const [rolesRes, accRes] = await Promise.all([
          apiFetch(GET_ALL_ROLES_API_URL, {
            headers: {
              Authorization: getAuthHeader(),
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }, "Failed to load roles.").catch(ignoreHttpErrors),

          apiFetch(APPROVAL_USERS_ACCOUNT_API_URL, {
            headers: {
              Authorization: getAuthHeader(),
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }, "Failed to load accounts.").catch(ignoreHttpErrors),
        ]);

        if (rolesRes?.ok) {
          setRoles(parseRoleOptions(await rolesRes.json()));
        }

        if (accRes?.ok) {
          setAccounts(parseAccountOptions(await accRes.json()));
        }
      } catch {
        setRoles([]);
        setAccounts([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (activeTab !== "approved") return;

    approved.forEach((u) => {
      if (u.assignedProjects.length === 0) return;
      if (projectsByUser[u.id]) return;

      if (isMultiAccountRole(u)) {
        if (u.assignedAccounts.length === 0) return;

        void fetchProjectsForAccounts(
          u.id,
          u.assignedAccounts,
        );
      } else {
        if (!u.assignedAccounts[0]) return;

        void fetchProjects(
          u.id,
          u.assignedAccounts[0],
        );
      }
    });
  }, [activeTab, approved]);

  const updateUserLocal = (id, patch) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === id
          ? {
            ...u,
            ...patch,
          }
          : u,
      ),
    );
  };

  const updateEditUser = (patch) => {
    setEditUser((prev) =>
      prev
        ? {
          ...prev,
          ...patch,
        }
        : prev,
    );
  };

  const fetchProjects = async (userId, accountId) => {
    if (!accountId) {
      setProjectsByUser((prev) => ({
        ...prev,
        [userId]: [],
      }));

      return;
    }

    setProjectsLoadingByUser((prev) => ({
      ...prev,
      [userId]: true,
    }));

    try {
      const res = await apiFetch(
        `${PROJECTS_BY_ACCOUNT_API_URL}/${encodeURIComponent(
          accountId,
        )}`,
        {
          headers: {
            Authorization: getAuthHeader(),
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
        "Failed to load projects.",
      );

      const parsedProjects = res.ok
        ? parseProjectOptions(await res.json())
        : [];

      setProjectsByUser((prev) => ({
        ...prev,
        [userId]: parsedProjects,
      }));
    } catch {
      setProjectsByUser((prev) => ({
        ...prev,
        [userId]: [],
      }));
    } finally {
      setProjectsLoadingByUser((prev) => ({
        ...prev,
        [userId]: false,
      }));
    }
  };

  /*
   * DM / Client Partner can be assigned multiple accounts.
   * Fetch projects belonging to all selected accounts.
   */
  const fetchProjectsForAccounts = async (
    userId,
    accountIds,
  ) => {
    const ids = (accountIds || [])
      .map((id) => String(id))
      .filter(Boolean);

    if (ids.length === 0) {
      setProjectsByUser((prev) => ({
        ...prev,
        [userId]: [],
      }));

      return;
    }

    setProjectsLoadingByUser((prev) => ({
      ...prev,
      [userId]: true,
    }));

    try {
      const results = await Promise.all(
        ids.map(async (accId) => {
          try {
            const res = await apiFetch(
              `${PROJECTS_BY_ACCOUNT_API_URL}/${encodeURIComponent(
                accId,
              )}`,
              {
                headers: {
                  Authorization: getAuthHeader(),
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
              },
              "Failed to load projects.",
            );

            return res.ok
              ? parseProjectOptions(await res.json())
              : [];
          } catch {
            return [];
          }
        }),
      );

      const merged = [];
      const seen = new Set();

      results.flat().forEach((p) => {
        const key = String(p.id);

        if (!seen.has(key)) {
          seen.add(key);
          merged.push(p);
        }
      });

      setProjectsByUser((prev) => ({
        ...prev,
        [userId]: merged,
      }));
    } finally {
      setProjectsLoadingByUser((prev) => ({
        ...prev,
        [userId]: false,
      }));
    }
  };

  const canApprove = (u) => {
    if (!u.role) return false;

    if (NO_ASSIGNMENT_ROLES.has(u.role)) {
      return true;
    }

    if (isMultiAccountRole(u)) {
      return u.assignedAccounts.length > 0 &&
        (u.alsoPm ? u.assignedProjects.length > 0 : true);
    }

    if (ACCOUNT_PROJECT_ROLES.has(u.role)) {
      return (
        Boolean(u.assignedAccounts[0]) &&
        u.assignedProjects.length > 0
      );
    }

    return false;
  };

  const updateServerRole = async (u, endpoint, method) => {
    const roleId = u.alsoPm
      ? DM_PM_ROLE_ID
      : Number(
        roles.find((r) => r.name === u.role)?.id ||
        u.roleId ||
        0,
      );

    const res = await apiFetch(endpoint, {
      method,
      headers: {
        Authorization: getAuthHeader(),
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        userId: u.uid,
        roleId,
        alsoPm: Boolean(u.alsoPm),
        assignedAccounts: NO_ASSIGNMENT_ROLES.has(u.role)
          ? [-1]
          : u.assignedAccounts,
        assignedProjects: requiresProjects(u)
          ? u.assignedProjects
          : [],
      }),
    }, "Failed to update user role.").catch(ignoreHttpErrors);

    return res?.ok ?? false;
  };

  const approveUser = async (u) => {
    if (!canApprove(u)) return;

    if (
      await updateServerRole(
        u,
        USER_UPDATE_API_BASE_URL,
        "POST",
      )
    ) {
      requestNotificationsRefresh();

      await fetchUsers();

      setActiveTab("approved");
    }
  };

  const saveEdit = async () => {
    if (!editUser) return;

    setSavingEdit(true);

    try {
      if (
        await updateServerRole(
          editUser,
          `${APPROVAL_USERS_API_URL}/edit`,
          "PUT",
        )
      ) {
        requestNotificationsRefresh();

        setEditUser(null);

        await fetchUsers();
      }
    } finally {
      setSavingEdit(false);
    }
  };

  const deactivate = async (u) => {
    const res = await apiFetch(USER_DEACTIVATE_API_URL, {
      method: "DELETE",

      headers: {
        Authorization: getAuthHeader(),
        "Content-Type": "application/json",
        Accept: "application/json",
      },

      body: JSON.stringify({
        userId: u.uid,
      }),
    }, "Failed to deactivate user.").catch(ignoreHttpErrors);

    if (res?.ok) {
      requestNotificationsRefresh();
      await fetchUsers();
    }
  };

  const resetLocked = async () => {
    if (!resetUser || !resetPassword.trim()) return;

    const res = await apiFetch(
      ADMIN_RESET_PASSWORD_API_URL,
      {
        method: "POST",

        headers: {
          Authorization: getAuthHeader(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },

        body: JSON.stringify({
          userId: resetUser.uid,
          password: resetPassword.trim(),
        }),
      },
      "Failed to reset password.",
    ).catch(ignoreHttpErrors);

    if (res?.ok) {
      setResetUser(null);
      setResetPassword("");

      await fetchUsers();
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box
        className="admin-user-management"
        sx={{
          px: {
            xs: 2,
            md: 3,
          },
          py: 2.5,
        }}
      >
        <DataState
          status={status}
          errorTitle="Couldn't load users"
          error={usersError}
          onRetry={() => void fetchUsers()}
          skeletonRows={6}
          skeletonCards={4}
          skeletonToolbar
        >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3.5,
            flexWrap: "wrap",
          }}
        >
          <KpiCard
            label="Total Users"
            count={users.length}
            sub="All registered accounts"
            color="#2563EB"
            bg={alpha("#2563EB", 0.1)}
            icon={
              <GroupsOutlinedIcon
                sx={{
                  fontSize: 20,
                  color: "#2563EB",
                }}
              />
            }
          />

          <KpiCard
            label="Pending Approval"
            count={pending.length}
            sub="Awaiting role assignment"
            color="#D97706"
            bg={alpha("#F59E0B", 0.12)}
            icon={
              <HourglassEmptyOutlinedIcon
                sx={{
                  fontSize: 20,
                  color: "#D97706",
                }}
              />
            }
            active={activeTab === "pending"}
            onClick={() => setActiveTab("pending")}
          />

          <KpiCard
            label="Approved Users"
            count={approved.length}
            sub="Active with assigned roles"
            color="#059669"
            bg={alpha("#10B981", 0.12)}
            icon={
              <CheckCircleOutlinedIcon
                sx={{
                  fontSize: 20,
                  color: "#059669",
                }}
              />
            }
            active={activeTab === "approved"}
            onClick={() => setActiveTab("approved")}
          />

          <KpiCard
            label="Locked Users"
            count={locked.length}
            sub="Failed Security Verification"
            color="#DC2626"
            bg={alpha("#EF4444", 0.12)}
            icon={
              <LockOutlinedIcon
                sx={{
                  fontSize: 20,
                  color: "#DC2626",
                }}
              />
            }
            active={activeTab === "locked"}
            onClick={() => setActiveTab("locked")}
          />
        </Box>

        <Card>
          <>
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderBottom: "1px solid #F1F5F9",
            }}
          >
            <TextField
              size="small"
              placeholder="Search by name or UST UID..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              sx={{
                minWidth: 260,
                flex: 1,
                maxWidth: 420,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        fontSize: 17,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              size="small"
              startIcon={<PersonOutlineOutlinedIcon />}
              onClick={() => setAddUserOpen(true)}
              sx={{
                borderRadius: "4px",
                px: 2,
                py: 0.8,
                fontWeight: 600,
                textTransform: "none",
                flexShrink: 0,
              }}
            >
              Add User
            </Button>
          </Box>

          <Box
            sx={{
              px: 2.5,
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, v) => {
                setActiveTab(v);
                setPage(1);
              }}
            >
              <Tab
                value="pending"
                label={`Pending (${pending.length})`}
                icon={
                  <HourglassEmptyOutlinedIcon
                    style={{
                      fontSize: "20px",
                    }}
                  />
                }
                iconPosition="start"
              />

              <Tab
                value="approved"
                label={`Approved (${approved.length})`}
                icon={
                  <CheckCircleOutlinedIcon
                    style={{
                      fontSize: "20px",
                    }}
                  />
                }
                iconPosition="start"
              />

              <Tab
                value="locked"
                label={`Locked (${locked.length})`}
                icon={
                  <LockOutlinedIcon
                    style={{
                      fontSize: "20px",
                    }}
                  />
                }
                iconPosition="start"
              />
            </Tabs>
          </Box>
          </>

          <>
          <TableContainer>
            <Table>
              <TableHead
                sx={{
                  "& .MuiTableCell-head": {
                    backgroundColor: "#EEF2F8",
                    borderBottom: "1px solid #DBE3EF",
                  },
                }}
              >
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>UST UID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned Role</TableCell>
                  <TableCell>Account & Project</TableCell>
                  <TableCell align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{
                        textAlign: "center",
                        py: 6,
                      }}
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginated.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{
                        textAlign: "center",
                        py: 6,
                      }}
                    >
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((u) => {
                    const projects =
                      projectsByUser[u.id] || [];

                    return (
                      <TableRow key={u.id}>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                              }}
                            >
                              {u.avatar}
                            </Avatar>

                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 160,
                              }}
                            >
                              {u.name || u.uid}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          {u.uid}
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={
                              statusChip[u.status].label
                            }
                            size="small"
                            sx={{
                              bgcolor:
                                statusChip[u.status].bg,
                              color:
                                statusChip[u.status].color,
                              border: `1px solid ${statusChip[u.status].border}`,
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          {activeTab === "pending" ? (
                            <AdminSelect
                              mode="multiple"
                              value={getSelectedRoles(u)}
                              placeholder="Select role"
                              options={roles.map((r) => ({
                                value: String(r.name),
                                label: r.name,
                                disabled: isRoleOptionDisabled(
                                  String(r.name),
                                  getSelectedRoles(u),
                                ),
                              }))}
                              onChange={(values) => {
                                const patch = getRoleChangePatch(
                                  u,
                                  values,
                                  roles,
                                );

                                updateUserLocal(u.id, patch);

                                if (patch.alsoPm && patch.assignedAccounts.length > 0) {
                                  void fetchProjectsForAccounts(
                                    u.id,
                                    patch.assignedAccounts,
                                  );
                                } else if (!patch.alsoPm) {
                                  setProjectsByUser((prev) => ({
                                    ...prev,
                                    [u.id]: [],
                                  }));
                                }
                              }}
                              className="admin-user-role-select"
                            />
                          ) : u.alsoPm ? (

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 0.5,
                                width: "100%",
                                py: 0.25,
                              }}
                            >
                              <Chip
                                label={DELIVERY_MANAGER}
                                size="small"
                              />
                              <Chip
                                label={PROJECT_MANAGER}
                                size="small"
                              />
                            </Box>
                          ) : u.role ? (
                            <Chip
                              label={u.role}
                              size="small"
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>

                        <TableCell>
                          {activeTab === "pending" ? (
                            NO_ASSIGNMENT_ROLES.has(u.role) ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: "italic" }}
                              >
                                No Account Assignment Required
                              </Typography>
                            ) : isMultiAccountRole(u) ? (
                              <Stack gap={1}>
                                <AdminSelect
                                  mode="multiple"
                                  value={u.assignedAccounts}
                                  placeholder="Assign account"
                                  options={accounts.map((a) => ({
                                    value: String(a.id),
                                    label: a.name,
                                  }))}
                                  onChange={(values) => {
                                    const nextAccounts = (values || []).map(String);

                                    updateUserLocal(u.id, {
                                      assignedAccounts: nextAccounts,
                                      assignedProjects: u.alsoPm
                                        ? u.assignedProjects
                                        : [],
                                    });

                                    if (u.alsoPm) {
                                      void fetchProjectsForAccounts(
                                        u.id,
                                        nextAccounts,
                                      );
                                    }
                                  }}
                                  tagRender={({ label, value }) => (
                                    <span className="admin-user-account-tag">
                                      {accountNameById.get(String(value)) || label}
                                    </span>
                                  )}
                                  className="admin-user-account-select"
                                />

                                {u.alsoPm && (
                                  <AdminSelect
                                    mode="multiple"
                                    value={u.assignedProjects}
                                    placeholder="Assign project"
                                    disabled={
                                      u.assignedAccounts.length === 0 ||
                                      projectsLoadingByUser[u.id]
                                    }
                                    options={projects.map((p) => ({
                                      value: String(p.id),
                                      label: p.name,
                                    }))}
                                    onChange={(values) =>
                                      updateUserLocal(u.id, {
                                        assignedProjects: (values || []).map(String),
                                      })
                                    }
                                    tagRender={({ label, value }) => (
                                      <span className="admin-user-project-tag">
                                        {projects.find(
                                          (p) => String(p.id) === String(value),
                                        )?.name || label}
                                      </span>
                                    )}
                                    className="admin-user-project-select"
                                  />
                                )}
                              </Stack>
                            ) : ACCOUNT_PROJECT_ROLES.has(u.role) ? (
                              <Stack gap={1}>
                                <AdminSelect
                                  value={u.assignedAccounts[0] || undefined}
                                  placeholder="Assign account"
                                  options={accounts.map((a) => ({
                                    value: String(a.id),
                                    label: a.name,
                                  }))}
                                  onChange={(value) => {
                                    const accId = String(value || "");

                                    updateUserLocal(u.id, {
                                      assignedAccounts: accId ? [accId] : [],
                                      assignedProjects: [],
                                    });

                                    void fetchProjects(u.id, accId);
                                  }}
                                  className="admin-user-account-select"
                                />

                                <AdminSelect
                                  mode="multiple"
                                  value={u.assignedProjects}
                                  placeholder="Assign project"
                                  disabled={
                                    !u.assignedAccounts[0] ||
                                    projectsLoadingByUser[u.id]
                                  }
                                  options={projects.map((p) => ({
                                    value: String(p.id),
                                    label: p.name,
                                  }))}
                                  onChange={(values) =>
                                    updateUserLocal(u.id, {
                                      assignedProjects: (values || []).map(String),
                                    })
                                  }
                                  tagRender={({ label }) => (
                                    <span className="admin-user-project-tag">
                                      {label}
                                    </span>
                                  )}
                                  className="admin-user-project-select"
                                />
                              </Stack>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: "italic" }}
                              >
                                Select role first
                              </Typography>
                            )
                          ) : NO_ASSIGNMENT_ROLES.has(u.role) ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: "italic" }}
                            >
                              No Account Assignment Required
                            </Typography>
                          ) : u.assignedAccounts.length + u.assignedProjects.length > 0 ? (
                            <Stack gap={0.75}>
                              {u.assignedAccounts.length > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.75,
                                  }}
                                >
                                  {u.assignedAccounts.map((accId) => (
                                    <Chip
                                      key={`acc-${u.id}-${accId}`}
                                      size="small"
                                      label={
                                        accountNameById.get(String(accId)) || accId
                                      }
                                      sx={{
                                        bgcolor: "#ECFDF3",
                                        color: "#027A48",
                                        border: "1px solid #86EFAC",
                                        fontWeight: 500,
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}

                              {u.assignedProjects.length > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.75,
                                  }}
                                >
                                  {u.assignedProjects.map((projectId) => {
                                    const projectName = (
                                      projectsByUser[u.id] || []
                                    ).find(
                                      (p) => String(p.id) === String(projectId),
                                    )?.name;

                                    return (
                                      <Chip
                                        key={`proj-${u.id}-${projectId}`}
                                        size="small"
                                        label={projectName || projectId}
                                        sx={{
                                          bgcolor: "#F5F3FF",
                                          color: "#6D28D9",
                                          border: "1px solid #C4B5FD",
                                          fontWeight: 500,
                                        }}
                                      />
                                    );
                                  })}
                                </Box>
                              )}
                            </Stack>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            whiteSpace: "nowrap",
                            width: 104,
                            verticalAlign: "middle",
                          }}
                        >
                          {activeTab === "pending" && (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={
                                <TaskAltOutlinedIcon />
                              }
                              onClick={() =>
                                void approveUser(u)
                              }
                              disabled={!canApprove(u)}
                            >
                              Approve
                            </Button>
                          )}

                          {activeTab === "approved" &&
                            (u.uid === currentUserUid ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            ) : (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.1,
                                  flexWrap: "nowrap",
                                }}
                              >
                                <IconButton
                                  size="small"
                                  sx={{
                                    p: 0.35,
                                  }}
                                  onClick={() => {
                                    const copy = {
                                      ...u,
                                      assignedAccounts: [
                                        ...u.assignedAccounts,
                                      ],
                                      assignedProjects: [
                                        ...u.assignedProjects,
                                      ],
                                    };

                                    setEditUser(copy);

                                    if (
                                      u.assignedAccounts[0] &&
                                      !projectsByUser[u.id]
                                    ) {
                                      if (u.alsoPm) {
                                        void fetchProjectsForAccounts(
                                          u.id,
                                          u.assignedAccounts,
                                        );
                                      } else if (
                                        ACCOUNT_PROJECT_ROLES.has(u.role)
                                      ) {
                                        void fetchProjects(
                                          u.id,
                                          u.assignedAccounts[0],
                                        );
                                      }
                                    }
                                  }}
                                >
                                  <EditOutlinedIcon
                                    sx={{
                                      fontSize: 17,
                                    }}
                                  />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  sx={{
                                    p: 0.35,
                                  }}
                                  onClick={() =>
                                    setDeleteUser(u)
                                  }
                                >
                                  <DeleteOutlineIcon
                                    sx={{
                                      fontSize: 17,
                                    }}
                                  />
                                </IconButton>
                              </Box>
                            ))}

                          {activeTab === "locked" && (
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() =>
                                setResetUser(u)
                              }
                            >
                              Reset
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              px: 2.5,
              py: 1.25,
              borderTop: "1px solid #F1F5F9",
            }}
          >
            <PillPagination
              currentPage={page}
              pageSize={rowsPerPage}
              total={filtered.length}
              itemLabel="users"
              onPageChange={setPage}
              pageSizeOptions={[5, 10, 25, 50]}
              onPageSizeChange={setRowsPerPage}
            />
          </Box>
          </>
        </Card>
        </DataState>
      </Box>

      {/* ADD USER DIALOG */}
      <Dialog
        className="trackon-root"
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: "16px",
            },
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1.1rem",
            borderBottom: "1px solid #E2E8F0",
            pb: 1.5,
          }}
        >
          Add User
        </DialogTitle>

        <DialogContent
          sx={{
            pt: "20px !important",
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
            }}
          >
            Create a new user account. The user will
            appear under Pending Approval after
            registration.
          </Typography>

          <RegistrationForm
            embedded
            onCancel={() => setAddUserOpen(false)}
            onSuccess={async () => {
              setAddUserOpen(false);

              setActiveTab("pending");
              setPage(1);

              await fetchUsers();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* EDIT USER DIALOG */}
      <Dialog
        className="trackon-root admin-user-management-dialog"
        open={Boolean(editUser)}
        onClose={() => setEditUser(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Edit User
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
            }}
          >
            Update role/assignments and save.
          </Typography>

          {editUser ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                mt: 1,
              }}
            >
              {/* ROLE */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  Role
                </Typography>

                <AdminSelect
                  mode="multiple"
                  value={getSelectedRoles(editUser)}
                  placeholder="Select role"
                  options={roles.map((r) => ({
                    value: String(r.name),
                    label: r.name,
                    disabled: isRoleOptionDisabled(
                      String(r.name),
                      getSelectedRoles(editUser),
                    ),
                  }))}
                  onChange={(values) => {
                    const patch = getRoleChangePatch(
                      editUser,
                      values,
                      roles,
                    );

                    updateEditUser(patch);

                    if (patch.alsoPm && patch.assignedAccounts.length > 0) {
                      void fetchProjectsForAccounts(
                        editUser.id,
                        patch.assignedAccounts,
                      );
                    } else if (!patch.alsoPm) {
                      setProjectsByUser((prev) => ({
                        ...prev,
                        [editUser.id]: [],
                      }));
                    }
                  }}
                  className="admin-user-edit-select"
                />

                {/* ASSIGNED ACCOUNTS */}
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    mt: 2,
                  }}
                >
                  Assigned Accounts
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mb: 1,
                  }}
                >
                  {editUser.assignedAccounts.length > 0
                    ? `${editUser.assignedAccounts.length} account${editUser.assignedAccounts.length === 1 ? "" : "s"} selected`
                    : "No accounts selected."}
                </Typography>

                {!NO_ASSIGNMENT_ROLES.has(
                  editUser.role,
                ) && (
                    <AdminSelect
                      mode={
                        isMultiAccountRole(editUser)
                          ? "multiple"
                          : undefined
                      }
                      value={
                        isMultiAccountRole(editUser)
                          ? editUser.assignedAccounts
                          : editUser.assignedAccounts[0] ||
                          undefined
                      }
                      placeholder="Add account"
                      options={accounts.map((a) => ({
                        value: String(a.id),
                        label: a.name,
                      }))}
                      onChange={(value) => {
                        if (isMultiAccountRole(editUser)) {
                          const nextAccounts = (value || []).map(String);

                          updateEditUser({
                            assignedAccounts: nextAccounts,
                            assignedProjects: editUser.alsoPm
                              ? editUser.assignedProjects
                              : [],
                          });

                          if (editUser.alsoPm) {
                            void fetchProjectsForAccounts(
                              editUser.id,
                              nextAccounts,
                            );
                          }

                          return;
                        }

                        const selected = String(
                          value || "",
                        );

                        if (!selected) return;

                        updateEditUser({
                          assignedAccounts: [selected],
                          assignedProjects: [],
                        });

                        if (
                          ACCOUNT_PROJECT_ROLES.has(
                            editUser.role,
                          )
                        ) {
                          void fetchProjects(
                            editUser.id,
                            selected,
                          );
                        }
                      }}
                      className="admin-user-edit-select"
                    />
                  )}
              </Box>

              {/* PROJECTS */}
              {requiresProjects(editUser) && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                    }}
                  >
                    Assigned Projects

                    {(editUser.alsoPm || ACCOUNT_PROJECT_ROLES.has(editUser.role)) && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 400,
                          ml: 0.5,
                        }}
                      >
                        (required)
                      </Typography>
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mb: 1,
                    }}
                  >
                    {editUser.assignedProjects.length > 0
                      ? `${editUser.assignedProjects.length} project${editUser.assignedProjects.length === 1 ? "" : "s"} selected`
                      : "No project selected."}
                  </Typography>

                  <AdminSelect
                    mode="multiple"
                    value={editUser.assignedProjects}
                    placeholder="Add project"
                    disabled={
                      !editUser.assignedAccounts[0] ||
                      (projectsByUser[
                        editUser.id
                      ] || []).length === 0
                    }
                    options={(
                      projectsByUser[
                      editUser.id
                      ] || []
                    ).map((p) => ({
                      value: String(p.id),
                      label: p.name,
                    }))}
                    onChange={(values) => {
                      updateEditUser({
                        assignedProjects:
                          (values || []).map(String),
                      });
                    }}
                    className="admin-user-edit-select"
                  />

                  {!editUser.assignedAccounts[0] && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                        display: "block",
                      }}
                    >
                      Select an account first to load
                      projects.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setEditUser(null)}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={() => void saveEdit()}
            disabled={!editUser || savingEdit || !canApprove(editUser)}
          >
            {savingEdit ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE USER DIALOG */}
      <Dialog
        open={Boolean(deleteUser)}
        onClose={() => setDeleteUser(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete User
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Are you sure you want to delete this user?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteUser(null)}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (!deleteUser) return;

              await deactivate(deleteUser);

              setDeleteUser(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* RESET PASSWORD DIALOG */}
      <Dialog
        open={Boolean(resetUser)}
        onClose={() => setResetUser(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Reset Password
        </DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            type="password"
            size="small"
            label="New Password"
            value={resetPassword}
            onChange={(e) =>
              setResetPassword(e.target.value)
            }
            sx={{
              mt: 1,
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setResetUser(null)}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={() => void resetLocked()}
            disabled={!resetPassword.trim()}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}